import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text, DropdownDownIcon, CheckIcon, DescriptionItem, Accordion, Card, CheckboxInput } from 'src/components';
import { colors } from "src/theme";
import { formatPrice, i18n, StandardizedCoverage } from "src/common";

const CoveragesHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 4px 0 4px;
`;

const TitleDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CoveragesTitle = styled.span`-
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: 'Red Hat Text', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${colors.text4};
`;

const CoveragesList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2px;
  background: ${colors.white};
`;

const CoverageItem = styled.div<{ selected: boolean }>`
  background: ${() => `${colors.background6}70`};
  border-radius: 4px;
  padding: 8px 16px;
  display: flex;
  gap: 4px;
  align-items: flex-start;
  opacity: ${({ selected }) => (!selected ? 0.64 : 1)};
`;

const CoverageTitleBox = styled.div`
  border-radius: 4px;
  padding: 4px;
  width: 100%;
`;

const CoverageDetailsBox = styled.div`
  background: ${colors.white};
  border-radius: 4px;
  padding: 10px;
  width: 100%;
`;

const RisksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RiskItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 10px;
`;

const ClaimsList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: 18px;
`;

const ClaimItemWrapper = styled.li`
  position: relative;
`;


export interface CoveragesAccordionProps {
  coverageGroups: Array<StandardizedCoverage>;
  handleToggleCalc: (code: string) => void,
  selectedAdditionalCoverages: { coverageCode: string, brandCode: string }[]
  alwaysOpen?: boolean
  disabled?: boolean
}

export const CoveragesAccordion = ({ 
  coverageGroups, 
  handleToggleCalc,
  selectedAdditionalCoverages,
  alwaysOpen = false,
  disabled = false
  
}: CoveragesAccordionProps) => {
  const [expandedCoverages, setExpandedCoverages] = useState<Set<string>>(new Set());

  const handleCoverageToggle = (coverageCode: string) => {
    setExpandedCoverages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(coverageCode)) {
        newSet.delete(coverageCode);
      } else {
        newSet.add(coverageCode);
      }
      return newSet;
    });
  };

  const earthquakeFranchise = coverageGroups.find((covGroup) => covGroup.code === "earthquakeFranchise");

  return (
    <Card padding={'0 0'}>
      <CoveragesHeader>
        <CoveragesTitle>{i18n.t('components.cards.offerCard.includedInPrice')}</CoveragesTitle>
      </CoveragesHeader>
      <CoveragesList>
        { coverageGroups.filter((cG) => disabled ? cG.code !== earthquakeFranchise?.code : true).map((coverageGroup: StandardizedCoverage) => (
          <CoverageItem key={coverageGroup.code} selected={disabled || coverageGroup.isIncluded || selectedAdditionalCoverages.find(cov => cov?.coverageCode === coverageGroup.code) !== undefined}>
                <Accordion
                  title={(
                    <Flex gap={8} align="center" justify='space-between' style={{ width: '100%' }}>
                      <Flex gap={8} align="center" style={{ width: '100%' }}>
                          { coverageGroup.isIncluded || disabled ? <CheckIcon color={colors.green1} width={20} height={20} /> :
                            <CheckboxInput 
                              left={true}
                              disabled={disabled}
                              noText={true}
                              value={Boolean(selectedAdditionalCoverages.find(sAC => sAC.coverageCode === coverageGroup.code))}
                              onChange={() => {
                                handleToggleCalc(coverageGroup.code)
                              }}
                            />
                          }
                      <TitleDetails onClick={() => handleCoverageToggle(coverageGroup.code)} style={{ flex: 1, flexDirection: 'row' }}>
                        <CoverageTitleBox>
                          <DescriptionItem
                            label={coverageGroup.name}
                            value={coverageGroup.isIncluded ? `${i18n.t('components.cards.offerCard.insuredSum')}: ${formatPrice(coverageGroup?.insuredAmount as number)}` 
                              : <Text type="small">{formatPrice(coverageGroup?.premium as number)}/{i18n.t('components.cards.offerCard.annually')}</Text>}
                            labelSize="medium"
                            valueSize='small'
                            labelColor={colors.text1}
                            bold={true}
                          />
                          { disabled && coverageGroup.code === 'earthquake' && earthquakeFranchise && coverageGroups.find((covGroup) => covGroup.code === earthquakeFranchise?.code ) ? <Flex gap={8} align="center" justify='space-between' style={{ width: '100%' }}>
                            <Flex gap={8} align="center" style={{ width: '100%' }}>
                              <CheckboxInput 
                                left={true}
                                noText={true}
                                value={Boolean(selectedAdditionalCoverages.find(sAC => sAC.coverageCode === earthquakeFranchise.code))}
                                onChange={() => {
                                  handleToggleCalc(Boolean(selectedAdditionalCoverages.find(sAC => sAC.coverageCode === earthquakeFranchise.code)) ? coverageGroup.code : earthquakeFranchise.code)
                                }}
                              />
                              <TitleDetails style={{ flex: 1, flexDirection: 'row' }}>
                                <CoverageTitleBox>
                                  <DescriptionItem
                                    label={earthquakeFranchise.name.split('Potres ')[1]}
                                    labelSize="medium"
                                    valueSize='small'
                                    labelColor={colors.text1}
                                    bold={true}
                                  />
                                  
                                </CoverageTitleBox>
                              </TitleDetails>
                            </Flex>
                          </Flex> : null}
                        </CoverageTitleBox>
                      </TitleDetails>
                    </Flex>
                    </Flex>
                  )}
                  content={(
                    <CoverageDetailsBox>  
                      <RisksList>
                        { coverageGroup?.risks ? coverageGroup.risks.map((risk, riskIndex) => (
                          <RiskItemWrapper key={riskIndex}>
                            { risk.claims && risk.claims.length > 0 && (
                              <>
                                <Text type="small" color={colors.text2}>{risk.name}</Text>
                                <ClaimsList>
                                  {risk.claims.map((claim, claimIndex) => (
                                    <ClaimItemWrapper key={claimIndex}>
                                      <Text type="small" color={colors.text2}>
                                        {claim.name} ({formatPrice(claim.insuredAmount)})
                                      </Text>
                                    </ClaimItemWrapper>
                                  ))}
                                </ClaimsList>
                              </>
                            )}
                            { risk.description && !risk.claims && (
                              <Text
                                type="small"
                                color={colors.text2}
                                dangerouslySetInnerHTML={{ __html: risk.description } as any}
                              />
                            )}
                          </RiskItemWrapper>
                        )) : (
                          <CoverageDetailsBox>
                            <Text 
                              type="small" 
                              color={colors.text2}
                              dangerouslySetInnerHTML={{ __html: coverageGroup.description! } as any}
                            />
                          </CoverageDetailsBox>
                        )}
                      </RisksList>
                    </CoverageDetailsBox>
                  )}
                  open={!disabled && (alwaysOpen || (coverageGroup?.risks || coverageGroup?.description !== undefined) && expandedCoverages.has(coverageGroup.code))}
                  expandable={false}
                  expandIcon={(coverageGroup?.risks !== undefined || coverageGroup?.description !== undefined)}
                  gap={4}
                  onChange={() => undefined}
                  expandIconComponent={!disabled && !alwaysOpen ? <DropdownDownIcon width={24} height={14} onClick={() => handleCoverageToggle(coverageGroup.code)} color={colors.text1} /> : <></>}
                  className="coverage-accordion"
                />
          </CoverageItem>
        ))}
      </CoveragesList>
    </Card>
  );
};